import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const GoogleButton = () => {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      onClick={handle}
      className="w-full flex items-center justify-center gap-3 rounded-full border border-border bg-background py-3 text-sm font-medium hover:bg-muted transition-colors"
      data-testid="google-auth-button"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
      </svg>
      Continue with Google
    </button>
  );
};

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = isLogin
        ? await login({ email: form.email, password: form.password })
        : await register(form);
      if (!user.paid) navigate("/checkout");
      else if (!user.onboarding_complete) navigate("/onboarding");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#040406] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 hero-radial opacity-70" />
        <Link to="/" className="relative flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
            <Plane className="h-4 w-4 text-white -rotate-45" />
          </div>
          <span className="font-display text-lg font-bold">sarkarijobai</span>
        </Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Stop studying randomly.
            <br />
            <span className="text-gradient">Start preparing with a system.</span>
          </h2>
          <p className="mt-5 text-white/50 max-w-sm leading-relaxed">
            Your AI exam coach turns your scores, mistakes and available time into a personalised daily
            preparation strategy.
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm text-white/60">
            <ShieldCheck className="h-5 w-5 text-amber-300" />
            Success Guarantee eligibility, subject to terms.
          </div>
        </div>
        <p className="relative text-xs text-white/30">AI-powered · Personalized · Exam-focused</p>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
              <Plane className="h-4 w-4 text-white -rotate-45" />
            </div>
            <span className="font-display text-lg font-bold">sarkarijobai</span>
          </Link>

          <h1 className="font-display text-2xl font-bold tracking-tight">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isLogin ? "Log in to continue your preparation." : "Start your sarkarijobai journey in minutes."}
          </p>

          <div className="mt-8">
            <GoogleButton />
            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm font-medium">Full name</label>
                <input
                  type="text" required value={form.name} onChange={set("name")}
                  className="mt-1.5 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Aarav Sharma" data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email" required value={form.email} onChange={set("email")}
                className="mt-1.5 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@example.com" data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="mt-1.5 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••" data-testid="auth-password-input"
              />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <button
              type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background font-medium py-3.5 hover:scale-[1.02] transition-transform disabled:opacity-60"
              data-testid="auth-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{isLogin ? "Log in" : "Create account"} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? "New to sarkarijobai? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} className="font-medium text-primary hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
